import React, { ComponentProps, useEffect, useRef, useState } from 'react';

import { Form, SonForm, StatusMessages, useForm } from '../../../_lib/Form2';
import { asFetchableView, useRefreshView, useViewContext } from '../../../_lib/ViewFetcher';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { PageInstantReactionButton, ReactionButtonParam } from '../partials/PageInstantReactionButton';
import { PageInstantReactionListView } from './PageInstantReactionListView';
import { ReactionType } from '../PageInstantReaction.types';
import { Form2Modifier } from '../../../_lib/Form2/Form';
import { StatusInfoModifier } from '../../../_lib/components/StatusInfo/partials/StatusInfo';
import { ButtonMinorAction } from '../../../components/molecules/Btn/ButtonWrapper';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { Icons } from '../../../components/atoms/SVG';
import { LazyView } from '../../../types/BlockWithViews';

type PageInstantReactionDefaultViewProps = {
  counts: Record<ReactionType, number>;
  reactionForm: SonForm<ReactionButtonParam>;
};

function PageInstantReactionDefault({ counts, reactionForm }: PageInstantReactionDefaultViewProps) {
  const { t } = useSonTranslation('PageInstantReaction');
  const { delayedRefreshView } = useRefreshView();
  const { reactionListView } = useViewContext();
  const hideSuccessMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const evaluationTranslation = t('evaluation.button');
  const reactionList = reactionListView as LazyView | undefined;

  const clearHideSuccessMessageTimer = () => {
    if (hideSuccessMessageTimerRef.current) {
      clearTimeout(hideSuccessMessageTimerRef.current);
    }
  };

  const { formProps, params, fetchState, resetFetchState } = useForm({
    form: reactionForm,
    onSuccess: () => {
      clearHideSuccessMessageTimer();
      hideSuccessMessageTimerRef.current = setTimeout(resetFetchState, 2000);
      delayedRefreshView();
    },
  });

  useEffect(() => {
    return () => {
      if (hideSuccessMessageTimerRef.current) {
        clearTimeout(hideSuccessMessageTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      <section className="page-instant-reaction" aria-labelledby="page-instant-reaction-section-heading">
        <h2 className="invisible" id="page-instant-reaction-section-heading">
          {t('section.heading')}
        </h2>
        <Form modifier={Form2Modifier.NO_PADDING} {...formProps}>
          <div className="page-instant-reaction__buttons-container">
            <ul className="page-instant-reaction__list">
              {params.getAllByType('button').map(param => (
                <li key={param.value} className="page-instant-reaction__list-item">
                  <PageInstantReactionButton {...param} />
                  <p className="font-regular-15-19">
                    {counts[param.value] ?? 0}
                    <span className="invisible">{t(`counts.${param.value}`)}</span>
                  </p>
                </li>
              ))}
            </ul>
            {reactionList && (
              <div className="page-instant-reaction__analysis">
                <ButtonMinorAction
                  icon={Icons.ANALYSIS}
                  text={evaluationTranslation}
                  onClick={() => setModalOpen(true)}
                />
              </div>
            )}
          </div>
          <StatusMessages
            errorModifier={[StatusInfoModifier.ALIGN_LEFT, StatusInfoModifier.PADDING_TOP, StatusInfoModifier.RED]}
            successModifier={[StatusInfoModifier.ALIGN_LEFT, StatusInfoModifier.PADDING_TOP]}
            sendingModifier={[StatusInfoModifier.ALIGN_LEFT, StatusInfoModifier.PADDING_TOP]}
            messageSuccess={t('status.success')}
            messageSending={t('status.sending')}
            messageFail={t('status.fail')}
            fetchState={fetchState}
          />
        </Form>
      </section>
      {modalOpen && reactionList && (
        <ModalDialog closeModal={() => setModalOpen(false)} headline={evaluationTranslation}>
          <PageInstantReactionListView {...reactionList} />
        </ModalDialog>
      )}
    </>
  );
}

export const PageInstantReactionDefaultView = asFetchableView(PageInstantReactionDefault);
export type PageInstantReactionDefaultView = ComponentProps<typeof PageInstantReactionDefaultView>;
export type { ReactionButtonParam } from '../partials/PageInstantReactionButton';
export type { ReactionType } from '../PageInstantReaction.types';
